"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import FontLoader from "@/components/font-loader";
import { useSiteSettings } from "@/components/site-settings-provider";

interface HeroSectionProps {
 badge?: string;
 badgeVariant?: "default" | "rainbow";
 title: string;
 highlight?: string;
 description?: string;
 variant?: "default" | "centered" | "split" | "dark";
 image?: string | null;
 visual?: React.ReactNode;
 primaryCta?: { label: string; href: string };
 secondaryCta?: { label: string; href: string };
}

function HeroBackground({ variant }: { variant: string }) {
 if (variant === "dark") {
 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[100px]" />
 <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-violet-600/8 rounded-full blur-[100px]" />
 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />
 </div>
 );
 }
 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/5 rounded-full blur-[80px]" />
 <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-violet-500/5 rounded-full blur-[80px]" />
 </div>
 );
}

export function HeroSection({
 badge,
 badgeVariant = "default",
 title,
 highlight,
 description,
 variant = "default",
 image,
 visual,
 primaryCta,
 secondaryCta,
	typography,
}: HeroSectionProps & { typography?: Record<string, unknown> }) {
 const { settings } = useSiteSettings();
 const siteTypography = (settings as any)?.typography || {};

 const headingFont = (typography && typeof (typography as any).headingFontFamily === "string"
   ? (typography as any).headingFontFamily
   : (siteTypography && (siteTypography.headingFontFamily || siteTypography.headingFont))) as string | undefined;
 const badgeClassName =
   badgeVariant === "rainbow"
     ? "inline-flex items-center gap-2 rounded-full border border-transparent bg-transparent px-0 py-0 text-sm font-semibold uppercase tracking-[0.26em] rainbow-text mb-6"
     : "inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm";
 const headingGradientColors = Array.isArray((typography as any)?.headingGradientColors)
   ? ((typography as any).headingGradientColors as string[])
   : Array.isArray(siteTypography?.headingGradientColors)
   ? (siteTypography.headingGradientColors as string[])
   : undefined;
 const headingGradient = headingGradientColors ? `linear-gradient(120deg, ${headingGradientColors.join(", ")})` : undefined;
 const useRainbow =
   ((typography && typeof (typography as any).headingStyle === "string" && ((typography as any).headingStyle as string).toLowerCase() === "rainbow") ||
     (siteTypography && typeof siteTypography.headingStyle === "string" && (siteTypography.headingStyle as string).toLowerCase() === "rainbow"));
  if (variant === "dark") {
    return (
    <section className="relative overflow-hidden bg-gray-950 py-14 lg:py-20">
 {headingFont ? <FontLoader font={headingFont} /> : null}
 <HeroBackground variant="dark" />
 <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="max-w-3xl">
 {badge && (
   <span className={badgeVariant === "rainbow" ? "inline-flex items-center gap-2 rounded-full border border-transparent bg-transparent px-0 py-0 text-sm font-semibold uppercase tracking-[0.26em] rainbow-text mb-6" : "inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6 backdrop-blur-sm"}>
     {badge}
   </span>
 )}
		<h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{
			color: (typography && typeof typography.headingColor === "string") ? typography.headingColor : undefined,
			textAlign: (typography && typeof typography.align === "string") ? (typography.align as any) : undefined,
      fontFamily: (typography && typeof typography.headingFontFamily === "string") ? typography.headingFontFamily : undefined,
		}}>
			<span className="text-white">{title}</span>
      {highlight && (
        <span
          className={useRainbow ? "rainbow-text" : "gradient-text"}
          style={headingGradient ? ({ ["--animated-spectrum" as any]: headingGradient } as any) : undefined}
        >
          {` ${highlight}`}
        </span>
      )}
		</h1>
		{description && (
			<p className="mt-6 text-lg leading-relaxed max-w-2xl" style={{ color: (typography && typeof typography.descriptionColor === "string") ? typography.descriptionColor : undefined, textAlign: (typography && typeof typography.align === "string") ? (typography.align as any) : undefined }}>{description}</p>
		)}
 {(primaryCta || secondaryCta) && (
 <div className="mt-8 flex flex-wrap gap-4">
 {primaryCta && (
 <Link href={primaryCta.href} className="cta-aurora-button inline-flex items-center rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-fuchsia-600 bg-[length:220%_220%] px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-600/45">
 {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
 </Link>
 )}
 {secondaryCta && (
 <Link href={secondaryCta.href} className="cta-secondary-button inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-all duration-300">
 {secondaryCta.label}
 </Link>
 )}
 </div>
 )}
 </div>
 </div>
 </section>
 );
 }

 if (variant === "split" && (image || visual)) {
  return (
   <section className="relative gradient-bg py-14 lg:py-20 overflow-hidden">
     {headingFont ? <FontLoader font={headingFont} /> : null}
     <HeroBackground variant="split" />
     <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       <div className="grid lg:grid-cols-2 gap-12 items-center">
         <div>
           {badge && (
             <span className={badgeClassName}>{badge}</span>
           )}
           <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
             {title}
             {highlight && (
               <span
                 className={useRainbow ? "rainbow-text" : "gradient-text"}
                 style={headingGradient ? ({ ["--animated-spectrum" as any]: headingGradient } as any) : undefined}
               >
                 {` ${highlight}`}
               </span>
             )}
           </h1>
           {description && <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{description}</p>}
           {(primaryCta || secondaryCta) && (
             <div className="mt-8 flex flex-wrap gap-4">
               {primaryCta && (
                 <Link href={primaryCta.href} className="cta-aurora-button inline-flex items-center rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-fuchsia-600 bg-[length:220%_220%] px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-600/45">
                   {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
                 </Link>
               )}
               {secondaryCta && (
                 <Link href={secondaryCta.href} className="cta-secondary-button inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-all duration-300">
                   {secondaryCta.label}
                 </Link>
               )}
             </div>
           )}
         </div>
         <div className="flex justify-center">
           {visual || (image && <img src={image} alt={title} className="rounded-2xl shadow-2xl max-h-96 w-full object-cover" />)}
         </div>
       </div>
     </div>
   </section>
 );
 }

 return (
   <section className="relative gradient-bg py-14 lg:py-20 overflow-hidden">
     {headingFont ? <FontLoader font={headingFont} /> : null}
     <HeroBackground variant="default" />
     <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       <div className={variant === "centered" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
         {badge && (
           <span className={badgeClassName}>{badge}</span>
         )}
         <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
           {title}
           {highlight && (
             <span
               className={useRainbow ? "rainbow-text" : "gradient-text"}
               style={headingGradient ? ({ ["--animated-spectrum" as any]: headingGradient } as any) : undefined}
             >
               {` ${highlight}`}
             </span>
           )}
         </h1>
         {description && (
           <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">{description}</p>
         )}
         {(primaryCta || secondaryCta) && (
           <div className="mt-8 flex flex-wrap gap-4">
             {primaryCta && (
               <Link href={primaryCta.href} className="cta-aurora-button inline-flex items-center rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-fuchsia-600 bg-[length:220%_220%] px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-600/45">
                 {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
               </Link>
             )}
             {secondaryCta && (
               <Link href={secondaryCta.href} className="cta-secondary-button inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-all duration-300">
                 {secondaryCta.label}
               </Link>
             )}
           </div>
         )}
       </div>
     </div>
   </section>
 );
}
